import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { ExternalLink, Search as SearchIcon } from "lucide-react";

import { Card } from "@/components/ui/Card";
import { FilterChip, ToolKindChip } from "@/components/ui/Badge";
import { SearchField } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Areas, Tools } from "@/api/resources";
import type { AreaOut, ToolOut, ToolStats } from "@/api/types";
import { formatDayMonth, parseDate, pluralRu } from "@/lib/format";

/** §6 — two orders, and the default is "what we actually use". */
type SortMode = "usage" | "name";

export function ToolsView() {
  const [params] = useSearchParams();
  const urlQuery = params.get("q") ?? "";
  const [filter, setFilter] = useState(urlQuery);
  const [sort, setSort] = useState<SortMode>("usage");

  // A ?q= arriving from a link seeds the box, but typing afterwards must win.
  // Adjusted during render (not in an effect) so the list never paints once
  // with the old query and again with the new one.
  const [lastUrlQuery, setLastUrlQuery] = useState(urlQuery);
  if (urlQuery !== lastUrlQuery) {
    setLastUrlQuery(urlQuery);
    setFilter(urlQuery);
  }

  const [toolsQ, areasQ, statsQ] = useQueries({
    queries: [
      { queryKey: ["tools"], queryFn: () => Tools.list() },
      { queryKey: ["areas"], queryFn: () => Areas.list() },
      // FEAT-025. Failing this must not take the registry with it — the list is
      // still perfectly usable without the usage line, so it is queried
      // separately and its error is simply an absent second line.
      { queryKey: ["tool-stats"], queryFn: () => Tools.stats(), staleTime: 5 * 60_000 },
    ],
  });

  const isLoading = toolsQ.isLoading || areasQ.isLoading;
  const isError = toolsQ.isError || areasQ.isError;
  const tools: ToolOut[] = useMemo(() => toolsQ.data ?? [], [toolsQ.data]);
  const areas: AreaOut[] = useMemo(() => areasQ.data ?? [], [areasQ.data]);
  const areaById = useMemo(() => new Map(areas.map((a) => [a.id, a])), [areas]);
  const statsBySlug = useMemo(
    () => new Map((statsQ.data ?? []).map((s) => [s.slug, s])),
    [statsQ.data],
  );

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matched = q
      ? tools.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.slug.toLowerCase().includes(q) ||
            t.tags.some((tag) => tag.toLowerCase().includes(q)),
        )
      : tools;

    const ordered = [...matched];
    if (sort === "name") {
      ordered.sort((a, b) => a.name.localeCompare(b.name, "ru"));
    } else {
      // §6 — busiest first. Ties fall back to the name so the order is stable
      // between refreshes; a list that reshuffles on every load is unreadable.
      ordered.sort((a, b) => {
        const an = statsBySlug.get(a.slug)?.journal_mentions ?? 0;
        const bn = statsBySlug.get(b.slug)?.journal_mentions ?? 0;
        if (an !== bn) return bn - an;
        return a.name.localeCompare(b.name, "ru");
      });
    }
    return ordered;
  }, [filter, tools, sort, statsBySlug]);

  return (
    <div className="space-y-4">
      <h1 className="text-screen font-bold text-text">Инструменты</h1>

      <SearchField
        value={filter}
        onChange={setFilter}
        placeholder="Поиск по имени, slug, тегам"
        icon={<SearchIcon className="h-4 w-4 shrink-0 text-hint" aria-hidden="true" />}
      />

      {/* §6 — the sort toggle, as chips (spec §7's filter chip). */}
      <div className="flex gap-1.5" role="group" aria-label="Порядок списка">
        <FilterChip active={sort === "usage"} onClick={() => setSort("usage")}>
          по использованию
        </FilterChip>
        <FilterChip active={sort === "name"} onClick={() => setSort("name")}>
          по имени
        </FilterChip>
      </div>

      {isLoading && <SkeletonList />}

      {isError && (
        <Card>
          <p>Ошибка загрузки инструментов.</p>
        </Card>
      )}

      {!isLoading && !isError && visible.length === 0 && (
        <Card>
          <p className="text-body text-hint">
            {tools.length === 0
              ? "Реестр пуст. Зарегистрируй первый инструмент через CLI: "
              : "Ничего не найдено по запросу. Попробуй другие слова."}
            {tools.length === 0 && <code>prosto-task new-tool</code>}
          </p>
        </Card>
      )}

      <ul className="space-y-2">
        {visible.map((t) => {
          const area = t.area_id ? areaById.get(t.area_id) ?? null : null;
          return (
            <li key={t.id}>
              <Card className="min-h-touch">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="truncate-line text-cell-title font-medium">{t.name}</h2>
                      <ToolKindChip kind={t.kind} />
                      {area && (
                        <span
                          className="rounded-badge border border-sep px-1.5 text-micro text-hint"
                          style={area.color ? { color: area.color } : undefined}
                        >
                          {area.name}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-chip text-hint">
                      <code>{t.slug}</code>
                      {t.version && <span className="ml-2">v{t.version}</span>}
                    </p>
                    {/* §6 — the usage line. */}
                    <UsageLine stats={statsBySlug.get(t.slug)} />
                    {t.description_md && (
                      <p className="clamp-3 mt-1 text-body">{t.description_md}</p>
                    )}
                  </div>
                  <a
                    href={t.repo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hit-44 inline-flex h-compact w-compact items-center justify-center rounded-compact text-accent"
                    aria-label={`Открыть репозиторий ${t.name}`}
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
                {t.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-badge bg-bg px-1.5 py-0.5 text-micro text-hint"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * FEAT-025 / §6 — "в журнале N · задач M · ошибок K · последний раз <дата>".
 *
 * All of it stays `--hint`: these are counters for orientation, not a verdict.
 * In particular the error count is NOT tinted `--dstr` — the flag sits on a
 * *decision* recorded in the journal, not on the tool, and colouring it red
 * would turn a neutral figure into an accusation the data does not support.
 *
 * A tool nobody has touched says so in words rather than printing "в журнале
 * 0": a newly registered tool is not a worse tool.
 */
function UsageLine({ stats }: { stats?: ToolStats }) {
  if (!stats) return null;
  if (stats.journal_mentions === 0 && stats.tasks_used === 0) {
    return <p className="mt-0.5 text-chip text-hint">Ещё не использовали</p>;
  }
  const last = parseDate(stats.last_used_at);
  const parts = [
    `в журнале ${stats.journal_mentions}`,
    `${pluralRu(stats.tasks_used, "задача", "задачи", "задач")} ${stats.tasks_used}`,
    `${pluralRu(stats.flagged_erroneous, "ошибка", "ошибки", "ошибок")} ${stats.flagged_erroneous}`,
  ];
  if (last) parts.push(`последний раз ${formatDayMonth(last)}`);
  return <p className="mt-0.5 text-chip text-hint">{parts.join(" · ")}</p>;
}
